import { Formik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import {
  FullMeetingWithEvaluationAndSelfReflectionFragment,
  MeetingStepStatus,
  MeetingStepViewId,
  SelfreflectionQuestion,
} from '../../../../types'
import '../../selfreflection/selfreflection.less'
import { oneSelfreflectionValueExists } from '../../selfreflection/selfreflections.functions'
import { SelfReflectionTopicCardsView } from '../../selfreflection/SelfreflectionTopicCardsView'
import useSelfreflections from '../../selfreflection/useSelfreflections'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { useAuth } from '../../../../context/auth-context'
import { isUserHrForCountry } from '../../../../shared/rolesUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingWithEvaluationAndSelfReflectionFragment
}

const AppraisalMeetingStepSelfReflection: FC<Props> = ({ backBtn, nextBtn, meeting }) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const { data, error, loading } = useSelfreflections(meeting.employee.id, meeting.id)

  if (
    loading ||
    error ||
    !data ||
    (!data.selfreflectionQuestions && (data.selfreflectionQuestions as SelfreflectionQuestion[]).length > 0)
  ) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.selfreflectionQuestions} error={error} showSpinner />
  }

  const isHrForCountry = isUserHrForCountry(meeting.appraisalSeason.country, user.roles)

  const selfReflectionAndEvaluationVisible: boolean = meeting.selfReflectionAndEvaluationVisible

  const initialValues = { form: data.selfreflectionQuestions as SelfreflectionQuestion[] }
  const employeePreparedSelfReflection = oneSelfreflectionValueExists(data.selfreflectionQuestions) && meeting.employeePrepared
  const notShared =
    employeePreparedSelfReflection && !(meeting.appraisal!.selfReflectionShared || selfReflectionAndEvaluationVisible || isHrForCountry)
  const shared =
    employeePreparedSelfReflection && (meeting.appraisal!.selfReflectionShared || selfReflectionAndEvaluationVisible || isHrForCountry)

  const employeeFirstName = meeting.employee.chosenFirstName ? meeting.employee.chosenFirstName : meeting.employee.legalFirstName

  const renderTitle = () => {
    if (!meeting.employeePrepared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.notPrepared'
    } else if (shared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.shared'
    } else if (notShared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.notShared'
    } else {
      return ''
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(_, { setSubmitting }) => {
        setSubmitting(false)
      }}
    >
      {() => {
        return (
          <div data-e2e-appraisal-step-selfreflection>
            <MeetingBackground>
              <PerText appearance="subtitle" color="primary" mb={!!renderTitle() ? 'm' : undefined}>
                {t(renderTitle(), {
                  employeeName: employeeFirstName,
                })}
              </PerText>

              {!employeePreparedSelfReflection && (
                <PerSpacer mb="xl">
                  <PerCardInfo
                    heading={t('appraisalMeeting.stepSelfReflection.manager.infoCard.heading.notPreparedMeeting', {
                      employeeName: employeeFirstName,
                    })}
                    icon={{ name: 'info_stroke' }}
                  />
                </PerSpacer>
              )}

              <PerText appearance="info" mb="xs">
                {employeePreparedSelfReflection
                  ? shared
                    ? t('appraisalMeeting.stepSelfReflection.manager.subheading.shared')
                    : ''
                  : t('appraisalMeeting.stepSelfReflection.manager.subheading.notPreparedMeeting')}
              </PerText>

              <SelfReflectionTopicCardsView
                isHrForCountry={isHrForCountry}
                meeting={meeting}
                alwaysVisible={selfReflectionAndEvaluationVisible}
              />
            </MeetingBackground>

            <PerFlexRow justify="space-between">
              {backBtn && backBtn(MeetingStepStatus.Done)}
              {nextBtn && nextBtn(MeetingStepStatus.Done)}
            </PerFlexRow>
            <AppraisalFlowHelper stepViewId={MeetingStepViewId.SelfReflection} stepStatus={MeetingStepStatus.Done} />
          </div>
        )
      }}
    </Formik>
  )
}
export default AppraisalMeetingStepSelfReflection
